/**
 * DatasetDetail — GVW-121 Dataset detail (collection + item)
 * 
 * Per brief.md: WorkspaceShell page with header card + Tabs (Overview / Assets / Lineage / 
 * Permissions / Audit / Preview). Right-aligned primary action: "Add to current view".
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { catalogClient, type Dataset, type CatalogResult } from '../../catalog/catalogClient';

interface DatasetDetailProps {
  className?: string;
}

type TabId = 'overview' | 'assets' | 'lineage' | 'permissions' | 'audit' | 'preview';

interface DatasetDetailState {
  dataset: Dataset | null;
  loading: boolean;
  error: string | null;
  activeTab: TabId;
}

export function DatasetDetail({ className }: DatasetDetailProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<DatasetDetailState>({
    dataset: null,
    loading: true,
    error: null,
    activeTab: 'overview',
  });

  const loadDataset = useCallback(async () => {
    if (!id) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'No dataset ID provided',
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    const result: CatalogResult<Dataset> = await catalogClient.getDataset(id);
    
    if (result.ok) {
      setState(prev => ({
        ...prev,
        dataset: result.data,
        loading: false,
      }));
    } else {
      let errorMessage = 'Failed to load dataset';
      if (result.reason === 'backend-not-deployed') {
        errorMessage = 'Backend not deployed. The dataset service is not available.';
      } else if (result.reason === 'not-found') {
        errorMessage = 'Dataset not found. It may have been removed or you may not have access.';
      } else if (result.reason === 'unauthorized') {
        errorMessage = 'You do not have access to this dataset.';
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [id]);

  useEffect(() => {
    loadDataset();
  }, [id, loadDataset]);

  const handleTabChange = (tabId: TabId) => {
    setState(prev => ({ ...prev, activeTab: tabId }));
  };

  const handleAddToCurrentView = () => {
    if (state.dataset) {
      // TODO: Implement add to current view
      console.log('Add dataset to current view:', state.dataset.id);
    }
  };

  if (state.loading) {
    return (
      <div className={`dataset-detail ${className || ''}`} aria-busy="true">
        <div className="dataset-detail__skeleton">
          <div className="dataset-detail__skeleton-header" />
          <div className="dataset-detail__skeleton-tabs" />
          <div className="dataset-detail__skeleton-content" />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`dataset-detail ${className || ''}`} role="alert">
        <div className="dataset-detail__error">
          <span className="material-icons" aria-hidden="true">error</span>
          <h1>We could not load this dataset</h1>
          <p>{state.error}</p>
          <button onClick={loadDataset} type="button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!state.dataset) {
    return (
      <div className={`dataset-detail ${className || ''}`}>
        <div className="dataset-detail__empty">
          <span className="material-icons" aria-hidden="true">dataset</span>
          <h1>Dataset not found</h1>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as TabId, label: 'Overview', icon: 'description' },
    { id: 'assets' as TabId, label: 'Assets', icon: 'inventory_2' },
    { id: 'lineage' as TabId, label: 'Lineage', icon: 'account_tree' },
    { id: 'permissions' as TabId, label: 'Permissions', icon: 'policy' },
    { id: 'audit' as TabId, label: 'Audit', icon: 'history' },
    { id: 'preview' as TabId, label: 'Preview', icon: 'preview' },
  ];

  return (
    <div className={`dataset-detail ${className || ''}`} data-testid="dataset-detail">
      {/* Header card */}
      <header className="dataset-detail__header">
        <div className="dataset-detail__header-content">
          <h1>
            <span className="material-icons" aria-hidden="true">dataset</span>
            {state.dataset.name}
          </h1>
          
          <div className="dataset-detail__header-chips">
            <span className="dataset-detail__chip dataset-detail__chip--type">
              Dataset
            </span>
            {state.dataset.properties?.license && (
              <span className="dataset-detail__chip dataset-detail__chip--license">
                {state.dataset.properties.license}
              </span>
            )}
          </div>
        </div>
        
        <div className="dataset-detail__header-actions">
          <button
            type="button"
            className="dataset-detail__add-button"
            onClick={handleAddToCurrentView}
            aria-label={`Add ${state.dataset.name} to current view`}
          >
            <span className="material-icons" aria-hidden="true">add</span>
            Add to current view
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="dataset-detail__tabs">
        <div className="dataset-detail__tab-list" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={state.activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              className={`dataset-detail__tab ${
                state.activeTab === tab.id ? 'dataset-detail__tab--active' : ''
              }`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="material-icons" aria-hidden="true">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab panels */}
        <div className="dataset-detail__tab-content">
          {state.activeTab === 'overview' && (
            <div
              id="tabpanel-overview"
              role="tabpanel"
              aria-labelledby="tab-overview"
              className="dataset-detail__tab-panel"
            >
              <h2>Overview</h2>
              
              {state.dataset.description && (
                <div className="dataset-detail__section">
                  <h3>Description</h3>
                  <p>{state.dataset.description}</p>
                </div>
              )}
              
              {state.dataset.bbox && (
                <div className="dataset-detail__section">
                  <h3>Spatial Extent</h3>
                  <p>
                    Longitude: {state.dataset.bbox[0].toFixed(6)} to {state.dataset.bbox[2].toFixed(6)}<br />
                    Latitude: {state.dataset.bbox[1].toFixed(6)} to {state.dataset.bbox[3].toFixed(6)}
                  </p>
                </div>
              )}
              
              {state.dataset.properties && (
                <div className="dataset-detail__section">
                  <h3>Properties</h3>
                  <dl className="dataset-detail__properties">
                    {Object.entries(state.dataset.properties).map(([key, value]) => (
                      <React.Fragment key={key}>
                        <dt>{key}</dt>
                        <dd>{String(value)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          )}

          {state.activeTab === 'assets' && (
            <div
              id="tabpanel-assets"
              role="tabpanel"
              aria-labelledby="tab-assets"
              className="dataset-detail__tab-panel"
            >
              <h2>Assets</h2>
              {state.dataset.assets ? (
                <div className="dataset-detail__assets" role="list">
                  {Object.entries(state.dataset.assets).map(([key, asset]) => (
                    <div key={key} className="dataset-detail__asset" role="listitem">
                      <h3>{key}</h3>
                      <p><strong>Type:</strong> {(asset as any)?.type || 'Unknown'}</p>
                      {(asset as any)?.href && (
                        <p><strong>URL:</strong> <a href={(asset as any).href} target="_blank" rel="noopener noreferrer">{(asset as any).href}</a></p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p>No assets available.</p>
              )}
            </div>
          )}

          {state.activeTab === 'lineage' && (
            <div
              id="tabpanel-lineage"
              role="tabpanel"
              aria-labelledby="tab-lineage"
              className="dataset-detail__tab-panel"
            >
              <h2>Lineage</h2>
              <p>Dataset lineage information will be displayed here.</p>
              <button
                type="button"
                onClick={() => {
                  // TODO: Navigate to full lineage page (GVW-125)
                  console.log('Navigate to lineage page for:', state.dataset?.id);
                }}
              >
                View Full Lineage
              </button>
            </div>
          )}

          {state.activeTab === 'permissions' && (
            <div
              id="tabpanel-permissions"
              role="tabpanel"
              aria-labelledby="tab-permissions"
              className="dataset-detail__tab-panel"
            >
              <h2>Permissions</h2>
              <p>Permission information will be displayed here.</p>
            </div>
          )}

          {state.activeTab === 'audit' && (
            <div
              id="tabpanel-audit"
              role="tabpanel"
              aria-labelledby="tab-audit"
              className="dataset-detail__tab-panel"
            >
              <h2>Audit Trail</h2>
              <p>Audit information will be displayed here.</p>
            </div>
          )}

          {state.activeTab === 'preview' && (
            <div
              id="tabpanel-preview"
              role="tabpanel"
              aria-labelledby="tab-preview"
              className="dataset-detail__tab-panel"
            >
              <h2>Preview</h2>
              <div className="dataset-detail__preview-placeholder">
                <span className="material-icons" aria-hidden="true">map</span>
                <p>Map preview will be displayed here.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}